import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import parsePhoneNumber from 'libphonenumber-js'
import { ParsedUrlQuery } from 'querystring'
import { When } from 'react-if'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { StarFilledIcon } from '@/atoms/Icons/StarFilledIcon'
import { StarOutlineIcon } from '@/atoms/Icons/StarOutlineIcon'
import { AsH3, CaptionMD, CaptionSM, Text, TitleXS } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { useThemeContext } from '@/contexts/ThemeContext'
import { useToggle } from '@/hooks/useToggle'
import { useTheaterPreferences } from '@/organisms/TheaterPreferences'
import { getSortedShowtimes } from '@/services/TicketsService'
import { useUser } from '@/services/UserService'
import { TheatricalReleaseObject, TheatricalShowtime, TheatricalShowtimeVenue } from '@/types/codegen-federation'
import { mergeUrlParams } from '@/utils/UriBuilder/UriBuilder'
import { useSafeAnalytics } from '@/utils/analytics'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { useLoginUrl } from '@/utils/auth-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { getStringFromSessionStorage, removeFromSessionStorage, writeToSessionStorage } from '@/utils/session-storage'
import { useTranslate } from '@/utils/translate/translate-client'
import { VenueLogo } from '@/views/TicketCheckoutViews/ReservationSummaryView/components/VenueLogo'
import ShowtimesList from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimesList'
import ShowtimesWithNoUrlsState from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimesWithNoUrlsState'
import { useTicketsPromoContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsPromo/TicketsPromoContext'
import { BaseTooltip } from '@/views/TicketCheckoutViews/ShowtimesView/components/ToolTips/ToolTip'
import { LocaleWarningModalState } from '@/views/TicketCheckoutViews/ShowtimesView/components/VenueList'
import VenuePills from '@/views/TicketCheckoutViews/ShowtimesView/components/VenuePills'
import { GroupTicketsModalTypes } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/GroupTicketsModal/GroupTicketsModal'
import { MarcusVenueWarning } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/MarcusVenueWarning'
import { sanitizePhoneNumber } from '@/views/TicketCheckoutViews/ShowtimesView/utils/utils'
import { generateTrackingPayload } from '@/views/TicketCheckoutViews/utils'
import { useTheatricalPromotionContext } from './TheatricalPromotionContext'
import { useTicketsContext } from './TicketsContext'

const autoScrollToVenue = ({ venueId, venueRef }: { venueId: string; venueRef: React.RefObject<HTMLLIElement> }) => {
  const sessionVenueId = getStringFromSessionStorage('venueId')
  if (sessionVenueId === venueId) {
    venueRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
    removeFromSessionStorage('venueId')
  }
}

export const VALID_PRIVATE_SHOWING_SLUGS = new Set([slugs.soundOfHopeTheStoryOfPossumTrot])

export interface VenueItemProps {
  venue: TheatricalShowtimeVenue
  query: ParsedUrlQuery
  theatricalRelease: TheatricalReleaseObject
  theaterSlug: string
  isGroupTicketsModalOpen?: boolean
  isBuyoutModalOpen?: boolean
  openGroupTicketsModal?: (type: GroupTicketsModalTypes) => void
  handleOpenAutoBookModal?: () => void
  handleOpenPreferencesModal?: () => void
  openBuyoutModal?: (buyoutVenue: TheatricalShowtimeVenue, theaterChain: string) => void
  setLocaleWarningModalState: React.Dispatch<React.SetStateAction<LocaleWarningModalState>>
  reservationId?: string
  showtimesDisabled: boolean
  setDisableShowtimes: React.Dispatch<React.SetStateAction<boolean>>
  zIndex: number
}

const regionToWarnWrongLanguage = new Set(['US', 'CA'])

export const VenueItem: FC<VenueItemProps> = ({
  venue,
  query,
  theaterSlug,
  theatricalRelease,
  reservationId,
  openGroupTicketsModal,
  openBuyoutModal,
  setLocaleWarningModalState,
  showtimesDisabled,
  setDisableShowtimes,
}) => {
  const { push, asPath } = useSafeRouter()
  const { track } = useSafeAnalytics()
  const { trackProductAdded } = useEcommerceEvents()
  const { userLocation, isLoggedIn } = useUser()
  const venueRef = useRef<HTMLLIElement>(null)
  const { t } = useTranslate('tickets')
  const { isDarkMode } = useThemeContext()
  const { experiments } = useTheatricalPromotionContext()

  const { latitude, longitude } = userLocation?.coordinates ?? {}
  const { guildTicketsCount, handleLanguageWarning, trackTicketIntent, detectLanguageDifference } = useTicketsContext()
  const { selectedVenueId, handleVenueSelect, handleSaveVenuePreferenceOnly } = useTheaterPreferences()
  const [selectedShowtimeTicketUrl, setSelectedShowtimeTicketUrl] = useState('')
  const [isExternalWarningModalOpen, toggleIsExternalWarningModalOpen] = useToggle(false)

  const redirectBackPath = useMemo(() => {
    return mergeUrlParams(asPath, { preferred_venue_id: venue.providerId })
  }, [asPath, venue.providerId])

  const { loginUrl, trackLoginStarted } = useLoginUrl(redirectBackPath)

  const promoContext = useTicketsPromoContext()
  const isAngelPartnerVenue = venue.ticketingProviderSlug === 'atom'

  const sortedShowTimes = useMemo(() => getSortedShowtimes(venue.showtimes), [venue.showtimes])
  const venuePhoneNumber = useMemo(
    () => parsePhoneNumber(sanitizePhoneNumber(venue?.phone), { defaultCountry: 'US' }),
    [venue?.phone],
  )

  const doShowtimesHavePurchaseUrl = venue.showtimes.every((s) => !!s?.ticketUrl)
  const countryCode = theatricalRelease?.region?.countryCode ?? 'US'
  const isPhilippinesFreeTicketFlow = query?.promo && countryCode === 'PH'
  const hasNoShowtimes = sortedShowTimes.length === 0
  const isVenueIntegrated = !!reservationId && !!theaterSlug && isAngelPartnerVenue
  const logoUrl = venue?.vendorLogo?.cloudinaryPath
  const isMarcusVenue = venue.name.includes('Marcus')

  const analyticsPayload = useMemo(() => {
    return {
      venue,
      latitude,
      longitude,
      theatricalName: theatricalRelease?.title as string,
      theatricalSlug: theatricalRelease?.theatricalSlug as string,
      projectSlug: theatricalRelease?.projectSlug as string,
    }
  }, [venue, theatricalRelease, latitude, longitude])

  const handleSendShowTimeClickEvents = useCallback(
    (showTime: TheatricalShowtime) => {
      const trackingPayload = generateTrackingPayload(showTime, analyticsPayload, reservationId)
      track('Tickets Showtime Selected', {
        ...trackingPayload,
      })
      track(`Tickets Showtime Selected ${theatricalRelease.theatricalSlug}`, {
        ...trackingPayload,
      })

      trackProductAdded({
        category: 'theatrical ticket',
        funnel: 'theatrical',
        sku: `ticket-${theatricalRelease?.theatricalSlug}`,
        product_id: `ticket-${theatricalRelease?.theatricalSlug}`,
        name: `${theatricalRelease?.theatricalSlug} theatrical ticket`,
        // ? if we could get the price for the showtime selected other wise this is
        // ! the value we are telling marketing the action is worth
        price: 15,
        currency: 'USD',
        quantity: 1,
        projectSlug: theatricalRelease?.theatricalSlug,
      })
      if (venue.ticketingProviderSlug === 'atom-tickets') {
        track('Integraged Tickets Showtime Selected', {
          ...trackingPayload,
        })
      } else {
        track('Non Integraged Tickets Showtime Selected', {
          ...trackingPayload,
        })
      }
      trackTicketIntent(analyticsPayload)
    },
    [
      analyticsPayload,
      reservationId,
      theatricalRelease.theatricalSlug,
      track,
      trackProductAdded,
      trackTicketIntent,
      venue.ticketingProviderSlug,
    ],
  )

  const handleAngelCheckoutNavigation = useCallback(
    (showTime: TheatricalShowtime) => {
      const reservableUrl = paths.tickets.checkout.seatMap(theaterSlug, showTime?.ticketingId, reservationId)
      const nonReservableUrl = paths.tickets.checkout.seatQuantitySelection(
        theaterSlug,
        showTime?.ticketingId,
        reservationId,
      )

      if (showTime?.reservable) {
        if (query?.flow) return push(mergeUrlParams(reservableUrl, { flow: query.flow as string }))
        if (query?.promo) return push(mergeUrlParams(reservableUrl, { flow: query.promo as string }))
        return push(reservableUrl)
      }

      if (query?.flow) return push(mergeUrlParams(nonReservableUrl, { flow: query.flow as string }))
      if (query?.promo) return push(mergeUrlParams(nonReservableUrl, { flow: query.promo as string }))
      return push(nonReservableUrl)
    },
    [theaterSlug, reservationId, query.flow, query.promo, push],
  )

  const handleShowtimeClick = useCallback(
    async (showTime: TheatricalShowtime, hasWarnedLanguage?: boolean) => {
      try {
        if (showtimesDisabled) return
        if (isVenueIntegrated) setDisableShowtimes(true)
        writeToSessionStorage('venueId', venue.id)

        const isInOtherLanguage = detectLanguageDifference(showTime)
        const skipLanguageWarning = !regionToWarnWrongLanguage.has(countryCode) || !!hasWarnedLanguage

        if (isMarcusVenue && showTime?.ticketUrl) {
          setSelectedShowtimeTicketUrl(showTime.ticketUrl)
          handleSendShowTimeClickEvents(showTime)
          return toggleIsExternalWarningModalOpen(true)
        }

        if (isInOtherLanguage && !skipLanguageWarning) {
          return handleLanguageWarning(setLocaleWarningModalState, showTime, () => {
            handleShowtimeClick(showTime, true)
          })
        }

        handleSendShowTimeClickEvents(showTime)

        if (isVenueIntegrated) return handleAngelCheckoutNavigation(showTime)
        promoContext?.handleOpenPromo({ promoContext, showTime, venue, trackingPayload: analyticsPayload })
      } catch (err) {
        setDisableShowtimes(false)
      }
    },
    [
      showtimesDisabled,
      isVenueIntegrated,
      setDisableShowtimes,
      venue,
      detectLanguageDifference,
      countryCode,
      isMarcusVenue,
      handleSendShowTimeClickEvents,
      handleAngelCheckoutNavigation,
      promoContext,
      analyticsPayload,
      toggleIsExternalWarningModalOpen,
      handleLanguageWarning,
      setLocaleWarningModalState,
    ],
  )

  useEffect(() => {
    autoScrollToVenue({ venueId: venue.id, venueRef: venueRef })
  }, [venue.id])

  const handleSetPreferredTheater = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation()
      if (isLoggedIn) {
        handleVenueSelect(venue.providerId, venue)
        handleSaveVenuePreferenceOnly(venue.providerId)
      } else {
        trackLoginStarted('preferred-venue-selection-showtimes-view')
        push(loginUrl)
      }
    },
    [handleSaveVenuePreferenceOnly, handleVenueSelect, venue, isLoggedIn, push, loginUrl, trackLoginStarted],
  )

  useEffect(() => {
    if (query?.preferred_venue_id && isLoggedIn) {
      if (venue.providerId === query?.preferred_venue_id && query?.preferred_venue_id !== selectedVenueId) {
        handleVenueSelect(query?.preferred_venue_id as string, venue)
        handleSaveVenuePreferenceOnly(query?.preferred_venue_id as string)
      }
    }
  }, [venue, query?.preferred_venue_id, selectedVenueId, handleVenueSelect, handleSaveVenuePreferenceOnly, isLoggedIn])

  const showPreferredVenueSelection = experiments?.['preferred_venue_selection_logged_out']
  return (
    <li
      ref={venueRef}
      className={classNames(
        'relative flex flex-col  border p-6 shadow-sm rounded-3xl',
        isDarkMode ? 'bg-core-gray-900' : 'bg-white border-core-gray-200',
      )}
    >
      <div className="justify-between gap-3 md:flex lg:flex-col xl:flex-row xl:items-start">
        <FlexColumn className="w-full gap-1">
          <div className="flex !items-start">
            {logoUrl && <VenueLogo className="mr-3 shrink-0" cloudinaryPath={logoUrl} />}
            <FlexRow className="w-full !items-start justify-between gap-2">
              <FlexColumn>
                <Text
                  as={AsH3}
                  className="photon-title-sm md:photon-title-md flex flex-wrap items-center gap-1.5"
                  color={isDarkMode ? 'white' : 'black'}
                  weight="bold"
                >
                  {venue?.name}
                  {!isPhilippinesFreeTicketFlow && hasNoShowtimes && (
                    <span
                      className={classNames('font-light photon-title-xs', isDarkMode ? 'text-white' : 'text-black')}
                    >
                      {t('noShowtimesParenthesis', '(No Showtimes)')}
                    </span>
                  )}
                </Text>
                <CaptionMD color={isDarkMode ? 'core-gray-400' : 'core-gray-700'}>
                  <span className={'!text-gray-600'}>{venue.addressText}</span>
                  {venuePhoneNumber && sortedShowTimes?.length === 0 && ` - ${venuePhoneNumber?.formatNational()}`}
                </CaptionMD>
              </FlexColumn>
              <When condition={!!isLoggedIn || !!showPreferredVenueSelection}>
                <BaseTooltip
                  buttonClassName="!rounded-full !p-2"
                  panelClassName={`!z-50 !rounded-xl`}
                  panelPosition="LEFT"
                  trigger={
                    <FlexRow className="items-center gap-2">
                      {isAngelPartnerVenue && (
                        <div className="relative cursor-pointer" onClick={handleSetPreferredTheater}>
                          <div className="relative">
                            {selectedVenueId === venue.providerId ? (
                              <StarFilledIcon size={24} color={'warning-500'} />
                            ) : (
                              <StarOutlineIcon size={24} color={'core-gray-400'} />
                            )}
                          </div>
                        </div>
                      )}
                    </FlexRow>
                  }
                  content={
                    <FlexColumn className="gap-2">
                      <FlexRow className="items-center gap-2">
                        <StarOutlineIcon size={24} color={'warning-500'} />
                        <TitleXS> {t('setPreferredTheater', 'Set Preferred Theater')}</TitleXS>
                      </FlexRow>
                      <CaptionSM color="core-gray-600">
                        {t(
                          'useStarIconToSetPreferredLocation',
                          'Use the star icon to set your preferred location for the best showtimes.',
                        )}
                      </CaptionSM>
                    </FlexColumn>
                  }
                />
              </When>
            </FlexRow>
          </div>
        </FlexColumn>
      </div>
      {!hasNoShowtimes && (
        <VenuePills
          theatricalSlug={theaterSlug}
          venue={venue}
          isAngelPartnerVenue={isAngelPartnerVenue}
          guildTicketsCount={guildTicketsCount}
          className="mt-6"
          noOnlineTicketing={!doShowtimesHavePurchaseUrl}
        />
      )}
      <ShowtimesList
        className="mt-6"
        countryCode={countryCode}
        venue={venue}
        reservationId={reservationId}
        theaterSlug={theaterSlug}
        showTimes={sortedShowTimes}
        venueId={venue?.id}
        onClick={handleShowtimeClick}
        shouldShowGroupTicketsButton={isAngelPartnerVenue && !!theatricalRelease?.region?.isGroupTicketsEnabled}
        shouldShowPrivateShowingButton={VALID_PRIVATE_SHOWING_SLUGS.has(theaterSlug)}
        venuePhoneNumber={venuePhoneNumber}
        analyticsPayload={analyticsPayload}
        openGroupTicketsModal={openGroupTicketsModal}
        openBuyoutModal={openBuyoutModal}
        isDisabled={showtimesDisabled}
      />
      {hasNoShowtimes && !doShowtimesHavePurchaseUrl && !isPhilippinesFreeTicketFlow && (
        <ShowtimesWithNoUrlsState
          venue={venue}
          venuePhoneNumber={venuePhoneNumber}
          analyticsPayload={analyticsPayload}
        />
      )}

      {isMarcusVenue && (
        <MarcusVenueWarning
          isOpen={isExternalWarningModalOpen}
          onClose={toggleIsExternalWarningModalOpen}
          onAccept={() => {
            push(selectedShowtimeTicketUrl)
          }}
        />
      )}
    </li>
  )
}
